import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, EllipsisVerticalIcon, PlusIcon } from '@heroicons/react/16/solid';
import { DocumentIcon } from '@heroicons/react/20/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Divider } from '../../../components/Divider';
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { Input } from '../../../components/Input';
import { Checkbox } from '../../../components/Checkbox';
import { AiReportHelperService } from '../../../services/appAiReportHelperService';
import { AiReportSavedTemplate, AiReportTemplateColumn } from '../../../models/AiReportHelper';
import showSuccessToast from '../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';

const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
}

const TemplatesPage: React.FC = () => {
    const [templates, setTemplates] = useState<AiReportSavedTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [sortOrder, setSortOrder] = useState('az');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');

    const [addOpen, setAddOpen] = useState(false);
    const [addStep, setAddStep] = useState<'upload' | 'select'>('upload');
    const [addName, setAddName] = useState('');
    const [addFile, setAddFile] = useState<File | null>(null);
    const [addUploading, setAddUploading] = useState(false);
    const [addFileMeta, setAddFileMeta] = useState<{ templateFileName: string; templateFileUrl: string } | null>(null);
    const [addHeaders, setAddHeaders] = useState<AiReportTemplateColumn[]>([]);
    const [addSelectedIds, setAddSelectedIds] = useState<Set<string>>(new Set());
    const [addSaving, setAddSaving] = useState(false);
    const addFileInputRef = useRef<HTMLInputElement>(null);

    const [renameTarget, setRenameTarget] = useState<AiReportSavedTemplate | null>(null);
    const [renameDraft, setRenameDraft] = useState('');
    const [renaming, setRenaming] = useState(false);

    const [viewTarget, setViewTarget] = useState<AiReportSavedTemplate | null>(null);

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
    };

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const res = await AiReportHelperService.listSavedTemplates();
            setTemplates(res.templates);
        } catch (err) {
            showErrorToast('Failed to load saved templates');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const availableYears = Array.from(
        new Set(
            templates
                .map((t) => new Date(t.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
    ];

    const filtered = templates
        .filter((t) => {
            const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStartDate = !filterStartDate || new Date(t.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(t.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(t.created_time).getFullYear().toString() === filterYear;
            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear;
        })
        .sort((a, b) => sortOrder === 'az' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));

    const handleOpenDeleteDialog = (id: number) => {
        setSelectedId(id);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (selectedId === null) return;
        setIsDeleting(true);
        try {
            await AiReportHelperService.deleteSavedTemplate(selectedId);
            setTemplates((prev) => prev.filter((t) => t.id !== selectedId));
            showSuccessToast('Template deleted');
            setIsOpen(false);
        } catch (err) {
            showErrorToast('Failed to delete template');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOpenAdd = () => {
        setAddStep('upload');
        setAddName('');
        setAddFile(null);
        setAddFileMeta(null);
        setAddHeaders([]);
        setAddSelectedIds(new Set());
        setAddOpen(true);
    };

    const handleAddFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) setAddFile(file);
    };

    const handleAddUpload = async () => {
        if (!addFile) return;
        setAddUploading(true);
        try {
            const res = await AiReportHelperService.uploadSavedTemplateFile(addFile);
            setAddFileMeta({ templateFileName: res.templateFileName, templateFileUrl: res.templateFileUrl });
            setAddHeaders(res.templateHeaders);
            setAddSelectedIds(new Set(res.templateHeaders.map((h) => h.id)));
            setAddStep('select');
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || "Couldn't read that file — try another .xlsx file.");
        } finally {
            setAddUploading(false);
        }
    };

    const handleToggleAddColumn = (id: string) => {
        setAddSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleSaveAdd = async () => {
        const name = addName.trim();
        if (!name || !addFileMeta || addSelectedIds.size === 0) return;
        setAddSaving(true);
        try {
            const selectedIds = addHeaders.filter((h) => addSelectedIds.has(h.id)).map((h) => h.id);
            const res = await AiReportHelperService.addSavedTemplate(name, addFileMeta.templateFileName, addFileMeta.templateFileUrl, addHeaders, selectedIds);
            setTemplates((prev) => [res.template, ...prev]);
            showSuccessToast(`Added template "${name}"`);
            setAddOpen(false);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to add this template');
        } finally {
            setAddSaving(false);
        }
    };

    const handleOpenRename = (template: AiReportSavedTemplate) => {
        setRenameTarget(template);
        setRenameDraft(template.name);
    };

    const handleSaveRename = async () => {
        if (!renameTarget) return;
        const name = renameDraft.trim();
        if (!name) return;
        setRenaming(true);
        try {
            const res = await AiReportHelperService.renameSavedTemplate(renameTarget.id, name);
            setTemplates((prev) => prev.map((t) => (t.id === res.template.id ? res.template : t)));
            setRenameTarget(null);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to rename that template');
        } finally {
            setRenaming(false);
        }
    };

    return (
        <>
            <Link
                to="/gp/ai-report-helper"
                className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
            >
                <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                Back to AI Report Helper
            </Link>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Saved Templates</Heading>
                </div>
                <Button onClick={handleOpenAdd}>
                    <PlusIcon />
                    Add template
                </Button>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search by name…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {!loading && filtered.length === 0 && (
                    <p className='py-16 text-center'>
                        No saved templates yet — save one from an active chat's mapping card.
                    </p>
                )}
                {!loading && filtered.map((t, index) => (
                    <li key={t.id} className="rounded-xl hover:bg-secondary">
                        <Divider soft={index > 0} />
                        <div className="flex items-center justify-between px-2">
                            <div className="flex gap-6 py-5">
                                <Avatar square initials={t.name[0]} className="bg-brand-teal/10 text-brand-teal size-12" />
                                <div>
                                    <div className="text-base/6 font-semibold">{t.name}</div>
                                    <div className="text-xs/6 text-zinc-600">
                                        {(t.template_headers || []).length} columns · {t.template_file_name} · saved {formatDate(t.created_time)}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <Dropdown>
                                    <DropdownButton plain aria-label="More options">
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor="bottom end">
                                        <DropdownItem onClick={() => setViewTarget(t)}>
                                            View
                                        </DropdownItem>
                                        <DropdownItem onClick={() => handleOpenRename(t)}>
                                            Edit
                                        </DropdownItem>
                                        <DropdownItem onClick={() => handleOpenDeleteDialog(t.id)}>
                                            Delete
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
            </FiltersDialog>

            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Template</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this saved template? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
                <DialogTitle>Add template</DialogTitle>
                <DialogBody>
                    {addStep === 'upload' && (
                        <div className="space-y-3">
                            <Text>Give it a name and upload the .xlsx file to save.</Text>
                            <Input
                                autoFocus
                                placeholder="Template name, e.g. Global Pharma Duo Daily Report"
                                value={addName}
                                onChange={(e) => setAddName(e.target.value)}
                            />
                            <input
                                ref={addFileInputRef}
                                type="file"
                                accept=".xlsx"
                                className="hidden"
                                onChange={handleAddFileChange}
                            />
                            <button
                                type="button"
                                className="flex w-full items-center gap-2 rounded-lg border border-dashed border-zinc-950/15 px-3 py-3 text-sm text-zinc-500 hover:border-violet-400 hover:text-violet-600 dark:border-white/15"
                                onClick={() => addFileInputRef.current?.click()}
                            >
                                <DocumentIcon className="size-5 shrink-0" />
                                {addFile ? addFile.name : 'Choose a .xlsx file'}
                            </button>
                        </div>
                    )}
                    {addStep === 'select' && (
                        <div className="space-y-3">
                            <Text>Choose which columns to keep for this template.</Text>
                            <div className="max-h-72 divide-y divide-zinc-950/5 overflow-y-auto rounded-lg border border-zinc-950/10 dark:divide-white/5 dark:border-white/10">
                                {addHeaders.map((col) => (
                                    <label key={col.id} className="flex items-center gap-2 px-3 py-2.5 text-sm text-zinc-950 dark:text-white">
                                        <Checkbox
                                            checked={addSelectedIds.has(col.id)}
                                            onChange={() => handleToggleAddColumn(col.id)}
                                        />
                                        {col.displayName || col.originalName}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setAddOpen(false)} disabled={addUploading || addSaving}>
                        Cancel
                    </Button>
                    {addStep === 'upload' && (
                        <Button onClick={handleAddUpload} disabled={addUploading || !addName.trim() || !addFile}>
                            {addUploading ? <Loading /> : 'Next'}
                        </Button>
                    )}
                    {addStep === 'select' && (
                        <Button onClick={handleSaveAdd} disabled={addSaving || addSelectedIds.size === 0}>
                            {addSaving ? <Loading /> : 'Save'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={!!renameTarget} onClose={() => setRenameTarget(null)}>
                <DialogTitle>Rename template</DialogTitle>
                <DialogBody>
                    <Input
                        autoFocus
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveRename()}
                    />
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setRenameTarget(null)} disabled={renaming}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveRename} disabled={renaming || !renameDraft.trim()}>
                        {renaming ? <Loading /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!viewTarget} onClose={() => setViewTarget(null)}>
                <DialogTitle>{viewTarget?.name}</DialogTitle>
                <DialogBody>
                    <Text className="mb-2 !text-zinc-500">{viewTarget?.template_file_name}</Text>
                    <ul className="max-h-80 divide-y divide-zinc-950/5 overflow-y-auto rounded-lg border border-zinc-950/10 dark:divide-white/5 dark:border-white/10">
                        {(viewTarget?.template_headers || []).map((col) => (
                            <li key={col.id} className="px-3 py-2 text-sm text-zinc-950 dark:text-white">{col.displayName || col.originalName}</li>
                        ))}
                    </ul>
                </DialogBody>
                <DialogActions>
                    <Button onClick={() => setViewTarget(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TemplatesPage;
